import {
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  input,
  InputSignal,
  model,
  ModelSignal,
  Signal,
  signal,
  WritableSignal,
  ChangeDetectionStrategy
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { GsSelectItem } from './models/select-item';

/**
 * The controller for a select-menu
 */
@Component({
  standalone: true,
  template: `
    <ng-content></ng-content>
  `,
  imports: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: GsSelectController
    }
  ]
})
export class GsSelectController<ValueType = number | string> implements ControlValueAccessor {
  // Services
  private readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);

  // Inputs & Outputs
  /** All items/links inside the dropdown-list */
  items: ModelSignal<GsSelectItem<ValueType>[]> = model<GsSelectItem<ValueType>[]>([]);
  /** Is this select currently opened? */
  opened: ModelSignal<boolean> = model<boolean>(false);
  /** Can ther be multiple elements selected? */
  multiple: InputSignal<boolean> = input<boolean>(false);
  /** Should the select-modal close if a item gets selected? */
  closeOnSelect: InputSignal<boolean> = input<boolean>(true);
  /** The placeholder text */
  placeholder: InputSignal<string> = input<string>('components.search.select');
  /** The text for the null-option, if not set the null-option will not be shown */
  nullOptionText: InputSignal<string> = input<string>('components.search.none');

  // Properties & Signals
  /** The initial absolute position of the select from the top */
  protected initialPositionTop: WritableSignal<number> = signal(0);
  /** The initial absolute position of the select from the left */
  protected initialPositionLeft: WritableSignal<number> = signal(0);
  /** Is the select disabled? */
  protected disabled: WritableSignal<boolean> = signal(false);
  /** Is at least one item selected? */
  isSelected: Signal<boolean> = computed(() => {
    return this.items().some((i) => i.selected);
  });
  /** Generates and returns the selected item/s */
  selectedItems: Signal<GsSelectItem<ValueType>[]> = computed(() => {
    const allItems = this.items();
    const selectedItems = allItems.filter((i) => i.selected);
    return selectedItems;
  });
  /** Generates and returns the select values as a readable text */
  selectedText: WritableSignal<string> = signal(this.placeholder());

  /**
   * Updates the selectedText signal based on current item selection
   */
  private updateSelectedText(): void {
    const selectedItems = this.items().filter((i) => i.selected);
    if (selectedItems.length <= 0) {
      this.selectedText.set(this.placeholder());
    } else {
      this.selectedText.set(selectedItems.map((i) => this.translate.instant(i.text)).join(', '));
    }
  }
  /**
   * Calulates the top position of the select-modal
   * @returns the top position of the select-modal
   */
  protected topPositionStyle: Signal<string> = computed(() => {
    return `${this.initialPositionTop()}px`;
  });
  /**
   * Calulates the left position of the select-modal
   * @returns the left position of the select-modal
   */
  protected leftPositionStyle: Signal<string> = computed(() => {
    return `${this.initialPositionLeft()}px`;
  });

  // Methods
  /**
   * Toggles the select open/closed
   * @param state new state of the select
   * @param event the event that triggered the toggle (click)
   */
  toggleSelect(state?: boolean, event?: Event) {
    const currentlyOpen = this.opened();
    const newState = state ?? !currentlyOpen;
    if ((this.disabled() || this.items().length === 0) && newState) return;
    this.onTouched();
    if (event) {
      event.stopPropagation();
      this.initialPositionLeft.set((event.target as HTMLElement).offsetLeft);
      this.initialPositionTop.set((event.target as HTMLElement).offsetTop + (event.target as HTMLElement).offsetHeight);
    }
    if (!newState && currentlyOpen) {
      this.triggerChange();
    }
    this.opened.set(newState);
  }

  /**
   * Selects the given item
   * @param item to select
   */
  selectItem(item: GsSelectItem<ValueType>, triggerChange?: boolean) {
    if (this.disabled() || item.disabled) return;
    if (this.multiple()) {
      this.items.update((items) => {
        const foundItem = items.find((i) => i.value === item.value);
        if (!foundItem) return items;
        foundItem.selected = !item.selected;
        return [...items];
      });
    } else {
      this.items.update((items) => {
        items.forEach((i) => (i.selected = i.value === item.value));
        return [...items];
      });
    }
    this.updateSelectedText();
    // Closing an open select emits the change; a closed one (e.g. rendered as a list) emits it directly
    if (this.closeOnSelect() && this.opened()) this.toggleSelect(false);
    else if (triggerChange) {
      this.onTouched();
      this.triggerChange();
    }
  }

  /**
   * Triggers the change-method of the ControlValueAccessor
   */
  private triggerChange() {
    this.onChange(
      this.items()
        .filter((i) => i.selected)
        .map((i) => i.value)
        .join(', ')
    );
  }

  // ControlValueAccessor methods
  // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
  private onChange(val: string) {}
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched() {}

  writeValue(val: string): void {
    const multiple = this.multiple();
    // A new array reference notifies the items signal, so selectedItems/isSelected recompute
    this.items.update((items) => {
      items.forEach(
        (i) =>
          (i.selected = multiple
            ? val?.includes(typeof i.value === 'string' ? i.value : (i.value?.toString() ?? ''))
            : i.value?.toString() === val?.toString())
      );
      return [...items];
    });
    // Update the selectedText signal to reflect the new selection
    this.updateSelectedText();
    // Trigger change detection because we mutated the items array
    this.cdr.markForCheck();
  }
  registerOnChange(fn: never): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: never): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
